import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { AuthService, IdToken } from '@auth0/auth0-angular';
import { take } from 'rxjs/operators';
import { States } from 'src/config/metadata-config';
import { FlexAddressValidatorComponent } from 'src/app/shared/components/flex-layout/flex-address-validator/flex-address-validator.component';
import { AccountService } from 'src/app/services/account.service';
import { emptyVerificationInterface, IVerificationInterface } from 'src/app/shared/models/verification.interface';
import { FlexAppService } from 'src/app/services/flex-app.service';
import { environment as ENV } from '../../../../environments/environment';
import { TermsandconditionsComponent } from '../termsandconditions/termsandconditions.component';
import { PrivacynoticeComponent } from '../privacynotice/privacynotice.component';
 
@Component({
  selector: 'upsc-flexapp',
  templateUrl: './flexapp.component.html',
  styleUrls: ['./flexapp.component.scss']
})
export class FlexappComponent implements OnInit {
  flexibleCoverageFormGroup: UntypedFormGroup;
  stateList: any[] = States;
  addressList = [];
  currentAddress = null;
  loading: boolean = false;
  denied: boolean = false;
  showAddressValidation: boolean = false;
  systemError: boolean = false;
  currentStep: number = 1;
  loading2: boolean = false;
  verifyCodeError: boolean = false;
  public createVerificationInterface: IVerificationInterface = emptyVerificationInterface;
  verifyCodeInterface = { emailId: "", toVerify: "" };
  emailAddress: string = '';
  formValidationError: boolean = false;
  policyNumber: string = '';
  public showPassword: boolean;
  dialogRefs: MatDialogRef<TermsandconditionsComponent>;
  dialogPrivacyRef: MatDialogRef<PrivacynoticeComponent>;
  isAddressVerified: boolean = false;
 
  locale: string = sessionStorage.getItem('locale') || 'en';
  auth0locale: string = sessionStorage.getItem('locale') || 'en';
 
  @ViewChild("name1") field1: ElementRef;
  @ViewChild("name2") field2: ElementRef;
  @ViewChild("name3") field3: ElementRef;
  @ViewChild("name4") field4: ElementRef;
  @ViewChild("name5") field5: ElementRef;
  @ViewChild("name6") field6: ElementRef;
 
  constructor(
    private accountService: AccountService,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private flexAppService: FlexAppService,
    private auth: AuthService
  ) {
    sessionStorage.setItem('userDetails', JSON.stringify({ type: 'flexApp' }));
    sessionStorage.setItem('ssp-landing-page', '/flexapp');
 
    this.flexibleCoverageFormGroup = this.formBuilder.group({
      upsStoreNumber: ['', Validators.compose([Validators.required])],
      upsStoreNumberReenter: ['', Validators.compose([Validators.required])],
      upsAccountNumber: ['', Validators.compose([Validators.required])],
      jobTitle: ['', Validators.compose([Validators.required])],
      firstName: ['', Validators.compose([Validators.required])],
      lastName: ['', Validators.compose([Validators.required])],
      emailAddress: ['', Validators.compose([Validators.required, Validators.email])],
      phoneNumber: ['', Validators.compose([Validators.required])],
      addressLineOne: ['', Validators.compose([Validators.required])],
      addressLineTwo: ['', Validators.compose([Validators.nullValidator])],
      city: ['', Validators.compose([Validators.required])],
      state: ['', Validators.compose([Validators.required])],
      zipCode: ['', Validators.compose([Validators.required])],
      cbTermsOfServices: ['', [(control) => {
        return !control.value ? { 'required': true } : null;
      }]],
      purchaseCoverage: [false, Validators.compose([Validators.nullValidator])],
      companyName: ['', Validators.compose([Validators.required])],
    });
 
    this.flexibleCoverageFormGroup.controls.upsStoreNumberReenter.valueChanges.subscribe(val => {
      if (val !== this.flexibleCoverageFormGroup.controls.upsStoreNumber.value) {
        this.flexibleCoverageFormGroup.controls.upsStoreNumberReenter.setErrors({ 'Mismatched Store Number': true });
      }
    });
 
    this.flexibleCoverageFormGroup.valueChanges.subscribe(() => {
      this.isAddressVerified = false;
    });
  }
 
  ngOnInit(): void {
    this.determineLoginState();
  }
 
  determineLoginState(): void {
    this.auth.idTokenClaims$.pipe(take(1)).subscribe(
      (response: IdToken | null | undefined) => {
        if (!response) {
          this.initiatePasswordlessAuth();
          return;
        }
 
        if (response != null && response.__raw) {
          sessionStorage.setItem('data', JSON.stringify(response));
          sessionStorage.setItem('verifyData', JSON.stringify({ ...response, emailAddress: response.email }));
          this.emailAddress = response.email;
 
          if (response.email && !this.email.value) {
            this.email.setValue(response.email);
          }
        }
      }
    );
  }
 
  initiatePasswordlessAuth(): void {
    const redirectUri = `${window.location.origin}/callback`;
    this.auth.loginWithRedirect({
      authorizationParams: {
        connection: 'email',
        redirect_uri: redirectUri,
        'ext-connect': 'email'
      },
      appState: { target: '/flexapp' }
    });
  }
 
  logout(): void {
    sessionStorage.removeItem('data');
    sessionStorage.removeItem('verifyData');
    this.auth.logout({
      logoutParams: {
        ui_locales: this.auth0locale,
        returnTo: ENV.auth0?.defaultLogoutUrl || window.location.origin,
        'ext-lang': this.locale,
        acr_values: this.locale
      }
    });
  }
 
  showCheckedHandler(cbEvent: boolean) {
    this.flexibleCoverageFormGroup.get("cbTermsOfServices").setValue(cbEvent);
  }
 
  dialogRef: any;
  validateAddress() {
    if (!this.flexibleCoverageFormGroup.valid) {
      this.validateAllFormFields(this.flexibleCoverageFormGroup);
      window.scrollTo(screen.width / 2, screen.height / 2);
      this.formValidationError = true;
      return false;
    }
 
    this.formValidationError = false;
    this.emailAddress = this.email.value;
 
    if (this.isAddressVerified) {
      this.submitFlexAppPolicy();
      return;
    }
 
    let authToken = '';
    try {
      const sessionData = sessionStorage.getItem('data');
      if (sessionData) {
        const parsedSession = JSON.parse(sessionData);
        authToken = parsedSession.__raw || parsedSession.token || '';
      }
    } catch (error) {
      console.error('Error reading session data:', error);
    }
 
    let config = new MatDialogConfig();
    config.autoFocus = false;
    config.closeOnNavigation = true;
    config.disableClose = true;
    config.width = '555px';
    config.data = {
      customerName: this.firstName.value + ' ' + this.lastName.value,
      addressLine1: this.addressLineOne.value,
      addressLine2: this.addressLineTwo.value,
      city: this.city.value,
      countrycode: 'US',
      zipcode: this.zip.value,
      state: this.state.value,
      companyName: this.companyName.value,
      token: authToken
    };
 
    this.dialogRef = this.dialog
      .open(FlexAddressValidatorComponent, config)
      .afterClosed().subscribe(result => {
        if (result === '0' || result === '-1' || !result) {
          return;
        }
 
        try {
          const storedAddress = sessionStorage.getItem("updatedVerifiedAddress");
          if (storedAddress && storedAddress !== "-1") {
            const updateAddress = JSON.parse(storedAddress);
 
            if (updateAddress?.AddressLine?.[0]) {
              this.addressLineOne.setValue(updateAddress.AddressLine[0], { emitEvent: false });
            }
            if (updateAddress?.PoliticalDivision2) {
              this.city.setValue(updateAddress.PoliticalDivision2, { emitEvent: false });
            }
            if (updateAddress?.PostcodePrimaryLow) {
              this.zip.setValue(updateAddress.PostcodePrimaryLow, { emitEvent: false });
            }
 
            const selectedState = this.stateList.find(x => x.state === updateAddress.PoliticalDivision1);
            if (selectedState) {
              this.state.setValue(selectedState.id, { emitEvent: false });
            }
          }
 
          this.isAddressVerified = true;
          this.submitFlexAppPolicy();
        } catch (e) {
          console.error('Error handling address validation result:', e);
        }
      });
  }
 
  submitFlexAppPolicy() {
    this.loading = true;
    let request = {
      jobTitle: this.jobTitle.value,
      firstName: this.firstName.value,
      lastName: this.lastName.value,
      addressLine1: this.addressLineOne.value,
      addressLine2: this.addressLineTwo.value,
      city: this.city.value,
      state: this.state.value,
      zip: this.zip.value,
      phone: this.phoneNumber.value,
      country: 'US',
      email: this.email.value,
      upsStoreNumber: this.upsStoreNumber.value,
      uspsShipperNumber: this.upsAccountNumber.value,
      triaCoverage: this.purchaseCoverage.value === true ? 'Elect' : 'Reject',
      companyName: this.companyName.value
    };
 
    this.flexAppService.retrieveCbpPolicy(request).subscribe(
      response => {
        this.loading = false;
        if (response && response !== null && response.policyNumber && response.policyNumber.length > 1) {
          this.policyNumber = response.policyNumber;
          this.currentStep = 3;
        } else {
          this.currentStep = 4;
        }
      },
      error => {
        this.loading = false;
        this.currentStep = 4;
      }
    );
  }
 
  validateAllFormFields(formGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }
 
  cancel() {}
 
  showChecked($event) {}
 
  updateAddress123(address) {
    if (address) {
      this.addressLineOne.setValue(address.AddressLine[0]);
      this.state.setValue(address.PoliticalDivision1);
      this.city.setValue(address.PoliticalDivision2);
      this.zip.setValue(address.PostcodePrimaryLow);
    }
  }
 
  openTermsandConditions() {
    let config = new MatDialogConfig();
    config.autoFocus = false;
    config.closeOnNavigation = true;
    config.width = '950px';
    this.dialogRefs = this.dialog.open(TermsandconditionsComponent, config);
    this.dialogRefs.afterClosed().subscribe(result => {});
  }
 
  openPrivacy() {
    let config = new MatDialogConfig();
    config.autoFocus = false;
    config.closeOnNavigation = true;
    config.width = '950px';
    this.dialogPrivacyRef = this.dialog.open(PrivacynoticeComponent, config);
    this.dialogPrivacyRef.afterClosed().subscribe(result => {});
  }
 
  gotoStepOne() {
    this.loading = false;
    this.loading2 = false;
    this.systemError = false;
    this.formValidationError = false;
    this.isAddressVerified = false;
    this.currentStep = 1;
  }
 
  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && charCode != 43 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
 
  get upsStoreNumber() { return this.flexibleCoverageFormGroup.controls.upsStoreNumber; }
  get upsAccountNumber() { return this.flexibleCoverageFormGroup.controls.upsAccountNumber; }
  get jobTitle() { return this.flexibleCoverageFormGroup.controls.jobTitle; }
  get firstName() { return this.flexibleCoverageFormGroup.controls.firstName; }
  get lastName() { return this.flexibleCoverageFormGroup.controls.lastName; }
  get email() { return this.flexibleCoverageFormGroup.controls.emailAddress; }
  get addressLineOne() { return this.flexibleCoverageFormGroup.controls.addressLineOne; }
  get addressLineTwo() { return this.flexibleCoverageFormGroup.controls.addressLineTwo; }
  get zip() { return this.flexibleCoverageFormGroup.controls.zipCode; }
  get city() { return this.flexibleCoverageFormGroup.controls.city; }
  get state() { return this.flexibleCoverageFormGroup.controls.state; }
  get companyName() { return this.flexibleCoverageFormGroup.controls.companyName; }
  get phoneNumber() { return this.flexibleCoverageFormGroup.controls.phoneNumber; }
  get purchaseCoverage() { return this.flexibleCoverageFormGroup.controls.purchaseCoverage; }
}
